import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, Clock, Zap, Shield, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Contact = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);

  return (
    <>
      <SEO
        title="Contact & Devis Gratuit — Châtelet"
        description="Contactez VistaWash pour votre devis detailing gratuit à Châtelet, Charleroi, Namur. Réponse rapide par WhatsApp, lun–sam 9h–18h. WhatsApp 0497 13 02 65."
        path="/contact"
      />

      {/* HERO */}
      <section data-testid="contact-hero" className="relative min-h-[85vh] flex items-end pt-40 pb-16 grain bg-black overflow-hidden">
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1605515298946-d062f2e9da53?auto=format&fit=crop&q=90&w=2400"
            alt="Atelier VistaWash"
            className="w-full h-[115%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        </motion.div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white bg-[#D90000] px-4 py-2 font-bold inline-block mb-8">
              Devis gratuit — Réponse 24h
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
            className="font-impact text-6xl sm:text-7xl md:text-8xl lg:text-[140px] leading-[0.88] max-w-5xl"
          >
            PARLONS DE <br />
            <span className="text-[#D90000]">VOTRE VÉHICULE.</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            className="text-white/75 text-base md:text-lg max-w-2xl leading-relaxed mt-8"
          >
            Trois façons simples de nous joindre. Choisissez celle qui vous convient — nous répondons personnellement, sans script commercial, sous 24 heures ouvrables.
          </motion.p>
        </div>
      </section>

      {/* CANAUX DE CONTACT */}
      <section data-testid="contact-channels" className="relative py-24 md:py-32 px-6 md:px-12 bg-black">
        <div className="max-w-[1500px] mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-16 md:mb-20 text-center">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Contact direct</span>
            <h2 className="font-impact text-5xl md:text-6xl lg:text-[90px] leading-[0.9] mt-6">
              JOIGNEZ-NOUS <br />
              <span className="text-[#D90000]">EN 1 CLIC.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-3">
            <ChannelCard
              icon={Phone}
              label="Téléphone"
              value={COMPANY.phone_display}
              subtitle="Réponse immédiate"
              href={COMPANY.phone_link}
              testid="contact-phone"
              index={0}
            />
            <ChannelCard
              icon={MessageCircle}
              label="WhatsApp"
              value="Envoyer un message"
              subtitle="Discussion + photos"
              href={COMPANY.whatsapp}
              external
              testid="contact-channel-whatsapp"
              index={1}
              highlight
            />
            <ChannelCard
              icon={Mail}
              label="Email"
              value={COMPANY.email}
              subtitle="Réponse rapide par WhatsApp, lun–sam 9h–18h"
              href={`mailto:${COMPANY.email}`}
              testid="contact-email"
              index={2}
            />
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS CONTACTER */}
      <section data-testid="contact-why" className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
        <div className="max-w-[1500px] mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-16 md:mb-20 max-w-3xl">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Notre engagement</span>
            <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
              CE QUE VOUS <br />
              <span className="text-[#D90000]">RECEVEZ.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-3">
            <PromiseBox
              icon={Zap}
              number="01"
              title="Réponse rapide"
              text="Nous répondons personnellement dans les 24 heures ouvrables. Aucun bot, aucun script."
              index={0}
            />
            <PromiseBox
              icon={Shield}
              number="02"
              title="Devis transparent"
              text="Prix ferme, sans frais cachés. Détail précis de chaque étape et des produits utilisés."
              index={1}
            />
            <PromiseBox
              icon={Clock}
              number="03"
              title="Rendez-vous sous 7 jours"
              text="Créneau disponible dans la semaine. Atelier à Châtelet, sur rendez-vous uniquement."
              index={2}
            />
          </div>
        </div>
      </section>

      {/* FORMULAIRE + INFOS */}
      <section data-testid="contact-form" className="relative py-24 md:py-32 px-6 md:px-12 bg-black border-t border-white/5">
        <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Formulaire</span>
              <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6 mb-8">
                DEMANDEZ <br />
                <span className="text-[#D90000]">VOTRE DEVIS.</span>
              </h2>
              <p className="text-white/60 leading-relaxed mb-10 max-w-md">
                Remplissez le formulaire ci-contre. Nous vous rappelons personnellement dans les 24 heures.
              </p>

              <div className="space-y-6 pt-6 border-t border-white/10">
                <InfoLine icon={MapPin} label="Atelier">
                  {COMPANY.address_line1}
                  <br />
                  {COMPANY.address_line2}
                </InfoLine>
                <InfoLine icon={Clock} label="Horaires">
                  Du lundi au samedi
                  <br />
                  <span className="text-white/50">09h — 18h · sur rendez-vous</span>
                </InfoLine>
                <InfoLine icon={Phone} label="Téléphone">
                  <a href={COMPANY.phone_link} className="hover:text-[#D90000] transition-colors">
                    {COMPANY.phone_display}
                  </a>
                </InfoLine>
              </div>
            </motion.div>
          </div>

          <div className="md:col-span-7">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <QuoteForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CARTE */}
      <section data-testid="contact-map" className="relative bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Nous trouver</span>
              <h2 className="font-impact text-4xl md:text-5xl lg:text-[64px] leading-[0.9] mt-6">
                ATELIER <span className="text-[#D90000]">VISTAWASH.</span>
              </h2>
              <p className="text-white/60 mt-4 max-w-xl">
                {COMPANY.address_full}
              </p>
            </div>
            <a
              href="https://www.google.com/maps?q=Rue+du+Cimeti%C3%A8re+3%2C+6200+Ch%C3%A2telet"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions"
              className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-4 transition-all border border-[#D90000] hover:border-white"
            >
              Itinéraire <ArrowUpRight size={16} />
            </a>
          </motion.div>

          <div className="aspect-[21/9] md:aspect-[21/8] border border-white/10 overflow-hidden">
            <iframe
              title="Localisation VistaWash"
              src="https://www.google.com/maps?q=Rue%20du%20Cimeti%C3%A8re%203%2C%206200%20Ch%C3%A2telet&output=embed"
              className="w-full h-full grayscale invert opacity-90"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </>
  );
};

const ChannelCard = ({ icon: Icon, label, value, subtitle, href, external, testid, index, highlight }) => (
  <motion.a
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    custom={index}
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    data-testid={testid}
    className={`relative overflow-hidden group min-h-[280px] flex flex-col justify-between p-10 transition-all ${
      highlight ? "bg-[#D90000] hover:bg-[#FF1A1A]" : "bg-[#0f0f0f] hover:bg-[#151515]"
    }`}
  >
    <div className="flex items-start justify-between">
      <Icon size={32} className={highlight ? "text-white" : "text-[#D90000] group-hover:scale-110 transition-transform"} />
      <ArrowUpRight size={20} className={`${highlight ? "text-white" : "text-white/40"} group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform`} />
    </div>

    <div>
      <div className={`text-[10px] uppercase tracking-[0.3em] mb-3 ${highlight ? "text-white/80" : "text-white/40"}`}>
        {label}
      </div>
      <div className={`font-impact text-3xl md:text-4xl leading-tight mb-2 ${highlight ? "text-white" : "text-white group-hover:text-[#D90000]"} transition-colors`}>
        {value}
      </div>
      <div className={`text-sm ${highlight ? "text-white/80" : "text-white/50"}`}>{subtitle}</div>
    </div>

    {/* Red accent line for non-highlight */}
    {!highlight && (
      <div className="absolute bottom-0 left-0 h-[3px] bg-[#D90000] w-0 group-hover:w-full transition-all duration-500" />
    )}
  </motion.a>
);

const PromiseBox = ({ icon: Icon, number, title, text, index }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    custom={index}
    data-testid={`promise-${number}`}
    className="relative p-10 bg-black border border-white/5 hover:border-[#D90000]/50 transition-colors group"
  >
    <div className="flex items-start justify-between mb-8">
      <div className="w-14 h-14 border-2 border-[#D90000] flex items-center justify-center text-[#D90000] group-hover:bg-[#D90000] group-hover:text-white transition-colors">
        <Icon size={24} />
      </div>
      <span className="font-impact text-xl text-white/30">{number}</span>
    </div>
    <h3 className="font-impact text-3xl md:text-4xl mb-4 leading-tight uppercase">{title}</h3>
    <p className="text-white/60 text-base leading-relaxed">{text}</p>
  </motion.div>
);

const InfoLine = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4">
    <Icon size={18} className="text-[#D90000] mt-1 shrink-0" />
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className="text-white/85 text-[15px] leading-relaxed">{children}</div>
    </div>
  </div>
);

export default Contact;
